import { useCallback, useEffect, useState } from 'react';
import { settingsApi } from '@/features/ai-dart/api/settings';
import { Aliases } from '@/features/ai-dart/format';
import type { Repo } from '@/features/ai-dart/types';
import { ActionButton, TABLE, TD, TH, THEAD, TR } from '@/features/ai-dart/components/controls';
import { ConfirmModal, Modal } from '@/shared/ui/Modal';
import { Field, FieldRow, FormActions, FormError, FormNote, Input, Select } from '@/features/ai-dart/components/form';

type Form = {
  id: number | null;
  repo: string;
  gitUrl: string;
  baseBranch: string;
  auto: Repo['auto'];
  aliasesText: string;
  branches: string[];
};

const EMPTY: Form = { id: null, repo: '', gitUrl: '', baseBranch: '', auto: 'deny', aliasesText: '', branches: [] };

const formOf = (row: Repo): Form => ({
  id: row.id, repo: row.repo, gitUrl: row.gitUrl ?? '', baseBranch: row.baseBranch ?? '',
  auto: row.auto, aliasesText: row.aliases.join(', '), branches: row.branches,
});

/**
 * 폼 → 서버 payload.
 *
 * `repo`는 **등록할 때만** 보낸다 — 서버 `update`가 받지 않는다(이름을 바꾸려면 지우고 다시 등록해야 한다).
 * `gitUrl`·`baseBranch`는 폼이 둘을 모두 들고 있으니 늘 함께 보낸다 — 빈칸이 곧 "비움"이다. 서버는 빈 문자열을
 * 지움으로, **생략을 미변경으로** 받는다(예전 서버는 생략도 지움으로 접어, 한쪽만 보낸 콘솔 밖 PATCH가 다른 쪽을 지웠다).
 */
function payloadOf(form: Form): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    gitUrl: form.gitUrl.trim(),
    baseBranch: form.baseBranch.trim(),
    auto: form.auto,
    aliases: Aliases.parse(form.aliasesText),
  };
  if (form.id === null) payload.repo = form.repo.trim();
  return payload;
}

export function ReposView(): React.JSX.Element {
  const [rows, setRows] = useState<Repo[]>([]); const [message, setMessage] = useState('');
  const load = useCallback(() => settingsApi.repos.list().then(setRows).catch((error: Error) => setMessage(error.message)), []);
  useEffect(() => { void load(); }, [load]);

  const [form, setForm] = useState<Form | null>(null);
  const [formError, setFormError] = useState('');
  const [formNote, setFormNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<Repo | null>(null);

  const set = (patch: Partial<Form>): void => setForm((current) => (current ? { ...current, ...patch } : current));
  const openForm = (next: Form): void => { setFormError(''); setFormNote(''); setForm(next); };
  /** `message`를 주는 조작(브랜치 감지)과 안 주는 조작(삭제)을 한 자리에서 받는다. */
  const run = async (work: () => Promise<{ ok: true; message?: string }>): Promise<void> => {
    setBusy(true);
    try { const result = await work(); setMessage(result.message ?? '저장했습니다.'); load(); }
    catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  };

  const save = async (): Promise<void> => {
    if (!form) return;
    setBusy(true); setFormError('');
    try {
      const payload = payloadOf(form);
      if (form.id) {
        await settingsApi.repos.update(form.id, payload);
        setMessage('저장했습니다.');
      } else {
        // 등록 직후 git URL이 있으면 브랜치를 바로 감지한다 — 그러지 않으면 base 브랜치를 고를 수 없다.
        const { id } = await settingsApi.repos.create(payload);
        setMessage('등록했습니다.');
        if (form.gitUrl.trim()) {
          try { const result = await settingsApi.repos.refresh(id); setMessage(`등록했습니다 — ${result.message}`); }
          catch { setMessage('등록했습니다 — 브랜치 감지는 실패했습니다. 목록의 "브랜치 감지"로 다시 시도하세요.'); }
        }
      }
      setForm(null); load();
    } catch (error) { setFormError((error as Error).message); }
    finally { setBusy(false); }
  };

  /** 모달 안에서 브랜치를 다시 감지해 base 브랜치 셀렉트를 채운다. */
  const refreshBranches = async (): Promise<void> => {
    if (!form?.id) return;
    setBusy(true); setFormError(''); setFormNote('');
    try {
      const result = await settingsApi.repos.refresh(form.id);
      const fresh = await settingsApi.repos.list();
      setRows(fresh);
      const found = fresh.find((row) => row.id === form.id);
      if (found) set({ branches: found.branches });
      setFormNote(result.message);
    } catch (error) { setFormError((error as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-lg border border-line bg-surface">
      <header className="flex items-center gap-3 border-b border-line p-4">
        <div>
          <h2 className="text-[13px] font-bold">레포 / 브랜치</h2>
          <p className="text-[11px] text-faint">별칭은 service_hint 귀속 키입니다. auto=allow는 자동수정과 단건 처리 허용을 뜻합니다.</p>
        </div>
        <div className="ml-auto">
          <ActionButton tone="primary" onClick={() => openForm(EMPTY)}>＋ 레포 등록</ActionButton>
        </div>
      </header>
      {message && <p className="border-b border-line bg-line-soft p-2 px-4 text-[12px]">{message}</p>}
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th className={TH}>레포</th>
              <th className={TH}>자동수정</th>
              <th className={TH}>base 브랜치</th>
              <th className={TH}>브랜치</th>
              <th className={TH}>별칭</th>
              <th className={TH}>Git URL</th>
              <th className={`${TH} text-right`}>조작</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={TR}>
                <td className={`${TD} font-medium`}>{row.repo}</td>
                <td className={TD}>
                  <span className={`rounded px-2 py-0.5 text-[11px] ${row.auto === 'allow' ? 'bg-emerald-50 text-emerald-700' : 'bg-line-soft text-muted'}`}>auto={row.auto}</span>
                </td>
                <td className={`${TD} font-mono`}>{row.baseBranch ?? <span className="font-sans text-faint">-</span>}</td>
                <td className={`${TD} text-muted`}>{row.branches.length}개</td>
                <td className={`${TD} text-muted`}>{row.aliases.join(', ') || <span className="text-faint">-</span>}</td>
                <td className={`${TD} max-w-[280px] truncate text-faint`} title={row.gitUrl ?? ''}>{row.gitUrl ?? 'Git URL 없음'}</td>
                <td className={TD}>
                  <div className="flex justify-end gap-1.5">
                    <ActionButton
                      disabled={busy || !row.gitUrl}
                      title={row.gitUrl ? '원격 브랜치를 다시 감지합니다' : 'Git URL을 먼저 저장하세요'}
                      onClick={() => void run(() => settingsApi.repos.refresh(row.id))}
                    >
                      브랜치 감지
                    </ActionButton>
                    <ActionButton onClick={() => openForm(formOf(row))}>수정</ActionButton>
                    <ActionButton tone="danger" onClick={() => setRemoving(row)}>삭제</ActionButton>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="p-8 text-center text-muted" colSpan={7}>등록된 레포가 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={form !== null} onClose={() => setForm(null)} title={form?.id ? `레포 수정 · ${form.repo}` : '레포 등록'}>
        {form && (
          <div>
            {form.id === null ? (
              <Field label="repo 이름 (고유)">
                <Input autoFocus value={form.repo} onChange={(event) => set({ repo: event.target.value })} placeholder="예: nd-market-api" />
              </Field>
            ) : (
              <Field label="repo 이름" hint="이름은 등록 후 바꿀 수 없습니다.">
                <Input value={form.repo} disabled />
              </Field>
            )}
            <Field label="Git URL">
              <Input value={form.gitUrl} onChange={(event) => set({ gitUrl: event.target.value })} placeholder="https://github.com/ORG/repo.git" />
            </Field>
            <FieldRow>
              <Field
                label="base 브랜치 (autofix PR base)"
                hint={form.branches.length === 0 ? '브랜치를 감지하기 전에는 직접 입력합니다.' : `감지된 ${form.branches.length}개 중에서 고릅니다.`}
              >
                {form.branches.length > 0 ? (
                  <Select value={form.baseBranch} onChange={(event) => set({ baseBranch: event.target.value })}>
                    <option value="">(지정 없음)</option>
                    {/* 저장된 값이 감지 목록에 없으면 옵션으로 얹는다 — 없으면 셀렉트가 조용히 값을 바꾼다. */}
                    {form.baseBranch && !form.branches.includes(form.baseBranch) && (
                      <option value={form.baseBranch}>{form.baseBranch} (현재 · 원격에 없음)</option>
                    )}
                    {form.branches.map((branch) => <option key={branch} value={branch}>{branch}</option>)}
                  </Select>
                ) : (
                  <Input value={form.baseBranch} onChange={(event) => set({ baseBranch: event.target.value })} placeholder="예: develop" />
                )}
              </Field>
              <Field label="자동수정 (auto)" hint="allow는 자동수정 PR과 단건 처리를 허용합니다.">
                <Select value={form.auto} onChange={(event) => set({ auto: event.target.value as Repo['auto'] })}>
                  <option value="deny">deny · 사람만</option>
                  <option value="allow">allow · 자동수정 허용</option>
                </Select>
              </Field>
            </FieldRow>
            <Field
              label="귀속 별칭 (aliases)"
              hint="인입의 service_hint가 이 값이나 repo 이름과 일치하면 이 레포로 귀속됩니다. 쉼표로 구분 — gcp의 ns/container, sentry project, 임의 별칭을 넣습니다."
            >
              <Input
                value={form.aliasesText}
                onChange={(event) => set({ aliasesText: event.target.value })}
                placeholder="예: pos, nd-prod/pos, uncle-pos"
              />
            </Field>
            {form.id === null && form.gitUrl.trim() && (
              <FormNote>Git URL이 있으니 <b>등록 직후 브랜치를 자동 감지</b>합니다. 그 다음 수정에서 base 브랜치를 고르세요.</FormNote>
            )}
            {formNote && <FormNote>{formNote}</FormNote>}
            {formError && <FormError message={formError} />}
            <FormActions>
              <ActionButton tone="primary" disabled={busy || (form.id === null && !form.repo.trim())} onClick={() => void save()}>
                {form.id ? '저장' : '등록'}
              </ActionButton>
              <ActionButton onClick={() => setForm(null)}>취소</ActionButton>
              {form.id !== null && (
                <ActionButton
                  disabled={busy || !form.gitUrl.trim()}
                  title={form.gitUrl.trim() ? '원격 브랜치를 다시 감지합니다' : 'Git URL을 먼저 저장하세요'}
                  onClick={() => void refreshBranches()}
                >
                  브랜치 감지
                </ActionButton>
              )}
            </FormActions>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={removing !== null}
        title="레포 삭제"
        confirmLabel="삭제"
        busy={busy}
        onClose={() => setRemoving(null)}
        onConfirm={() => { const row = removing; setRemoving(null); if (row) void run(() => settingsApi.repos.remove(row.id)); }}
      >
        <strong>{removing?.repo}</strong>을 삭제합니다. 별칭 {removing?.aliases.length ?? 0}개가 함께 지워져,
        이 레포로 귀속되던 인입이 <strong>귀속 미상</strong>으로 떨어집니다.
      </ConfirmModal>
    </section>
  );
}
